import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plug2, Plus, CheckCircle2, XCircle, AlertCircle, Clock,
  RefreshCw, Trash2, Settings, Search, Users, Server,
  HardDrive, Shield, MessageSquare, Bell, Eye, Globe,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../api/client';
import { Integration, IntegrationTypeDef, IntegrationStatus } from '../types';

const STATUS_CONFIG: Record<IntegrationStatus, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  connected: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Connected' },
  disconnected: { icon: XCircle, color: 'text-gray-400', label: 'Disconnected' },
  error: { icon: AlertCircle, color: 'text-red-400', label: 'Error' },
  pending: { icon: Clock, color: 'text-yellow-400', label: 'Pending' },
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Users, Server, HardDrive, Shield, MessageSquare, Bell, Eye, Globe, Plug2,
};

export function Integrations() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState<IntegrationTypeDef | null>(null);

  const { data: integrations = [], isLoading } = useQuery<Integration[]>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const res = await api.get('/integrations');
      return res.data.data;
    },
  });

  const { data: catalogTypes = [] } = useQuery<IntegrationTypeDef[]>({
    queryKey: ['integration-types'],
    queryFn: async () => {
      const res = await api.get('/integrations/catalog/types');
      return res.data.data;
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/${id}/test`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      const { connected, latencyMs } = res.data.data;
      toast[connected ? 'success' : 'error'](
        connected ? `Connected (${latencyMs}ms)` : 'Connection failed',
      );
    },
    onError: () => toast.error('Test failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['integrations'] }); toast.success('Integration removed'); },
  });

  const categories = [...new Set(catalogTypes.map((t) => t.category))];

  const filteredTypes = catalogTypes.filter((t) => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = !selectedCategory || t.category === selectedCategory;
    return matchSearch && matchCat;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Connect your enterprise systems to Orbayx automation</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-sm text-white font-medium transition-all shadow-glow"
        >
          <Plus className="w-4 h-4" /> Add Integration
        </button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 px-4 py-3 bg-white border border-gray-200 rounded-xl">
        <div className="text-sm">
          <span className="text-white font-semibold">{integrations.length}</span>
          <span className="text-gray-500 ml-1">configured</span>
        </div>
        <div className="w-px h-4 bg-gray-100" />
        <div className="text-sm">
          <span className="text-emerald-400 font-semibold">{integrations.filter((i) => i.status === 'connected').length}</span>
          <span className="text-gray-500 ml-1">connected</span>
        </div>
        <div className="w-px h-4 bg-gray-100" />
        <div className="text-sm">
          <span className="text-red-400 font-semibold">{integrations.filter((i) => i.status === 'error').length}</span>
          <span className="text-gray-500 ml-1">errors</span>
        </div>
        <div className="w-px h-4 bg-gray-100" />
        <div className="text-sm">
          <span className="text-white font-semibold">{catalogTypes.length}</span>
          <span className="text-gray-500 ml-1">available connectors</span>
        </div>
      </div>

      {/* Active Integrations */}
      {integrations.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-600 mb-3">Your Integrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map((integration) => {
              const status = STATUS_CONFIG[integration.status];
              const StatusIcon = status.icon;
              return (
                <div key={integration.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-orbayx-500/10 rounded-lg">
                        <Plug2 className="w-4 h-4 text-orbayx-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{integration.name}</div>
                        <div className="text-xs text-gray-400">{integration.type.replace(/_/g, ' ')}</div>
                      </div>
                    </div>
                    <div className={clsx('flex items-center gap-1 text-xs', status.color)}>
                      <StatusIcon className="w-3.5 h-3.5" />
                      {status.label}
                    </div>
                  </div>
                  {integration.lastTestedAt && (
                    <div className="text-xs text-gray-400 mb-3">
                      Last tested: {new Date(integration.lastTestedAt).toLocaleString()}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => testMutation.mutate(integration.id)}
                      disabled={testMutation.isPending}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-500 hover:text-gray-900 transition-all"
                    >
                      <RefreshCw className="w-3 h-3" /> Test
                    </button>
                    <button className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-500 hover:text-gray-900 transition-all">
                      <Settings className="w-3 h-3" /> Configure
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(integration.id)}
                      className="ml-auto flex items-center gap-1 px-2.5 py-1.5 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 rounded-lg text-xs text-gray-400 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connector Catalog */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-600">Available Connectors</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search connectors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500 transition-all w-48"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:border-orbayx-500 transition-all"
            >
              <option value="">All Categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filteredTypes.map((type) => {
            const IconComp = ICON_MAP[type.icon] || Plug2;
            const isConnected = integrations.some((i) => i.type === type.type && i.status === 'connected');
            return (
              <button
                key={type.type}
                onClick={() => { setSelectedType(type); setShowAddModal(true); }}
                className={clsx(
                  'flex items-start gap-3 p-3 rounded-xl border text-left hover:bg-gray-50 transition-all',
                  isConnected ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-gray-200 bg-white',
                )}
              >
                <div className="p-2 bg-orbayx-500/10 rounded-lg flex-shrink-0 mt-0.5">
                  <IconComp className="w-4 h-4 text-orbayx-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-700 truncate">{type.name}</span>
                    {isConnected && <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
                  </div>
                  <div className="text-[10px] text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">{type.description}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {type.authMethods.slice(0, 2).map((m) => (
                      <span key={m} className="px-1.5 py-0.5 bg-gray-50 rounded text-[9px] text-gray-400">{m}</span>
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
